import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh, Send, PlayArrow } from '@mui/icons-material';
import {
  listWhatsappPaymentReminders,
  runWhatsappReminderBatch,
  approveWhatsappReminder,
  rejectWhatsappReminder,
  sendWhatsappReminder,
  approveAndSendWhatsappReminder,
  WhatsappPaymentReminderRow,
} from '../../../services/request/WhatsappPaymentReminders';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { toast } from 'react-toastify';
import { PageHeader, DataToolbar, EmptyState } from '../../../shared-components/ui';

const STATUS_OPTIONS = [
  'ALL',
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
  'SENT',
  'FAILED',
] as const;

const statusColor = (s: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (s) {
    case 'PENDING_APPROVAL':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'SENT':
      return 'success';
    case 'FAILED':
    case 'REJECTED':
      return 'error';
    default:
      return 'default';
  }
};

const WhatsappPaymentRemindersAdmin: React.FC = () => {
  const [rows, setRows] = useState<WhatsappPaymentReminderRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING_APPROVAL');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<WhatsappPaymentReminderRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<WhatsappPaymentReminderRow | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listWhatsappPaymentReminders(page, rowsPerPage, statusFilter);
      setRows(Array.isArray(res?.content) ? res.content : []);
      setTotal(typeof res?.totalElements === 'number' ? res.totalElements : 0);
    } catch {
      toast.error('Failed to load WhatsApp reminders');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunBatch = async () => {
    try {
      const r = await runWhatsappReminderBatch();
      toast.success(`Batch run: ${r.created} new reminder(s) for ${r.batchRunDate}`);
      load();
    } catch {
      toast.error('Failed to run reminder batch');
    }
  };

  const wrapAction = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    try {
      setActionId(id);
      await fn();
      toast.success(ok);
      load();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      toast.error(msg || 'Action failed');
    } finally {
      setActionId(null);
    }
  };

  const openReject = (row: WhatsappPaymentReminderRow) => {
    setRejectTarget(row);
    setRejectReason('');
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    await wrapAction(
      rejectTarget.id,
      () => rejectWhatsappReminder(rejectTarget.id, rejectReason || undefined),
      'Reminder rejected'
    );
    setRejectOpen(false);
    setRejectTarget(null);
  };

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto' }}>
      <PageHeader
        eyebrow="Lending · Collections"
        title="WhatsApp payment reminders"
        subtitle="Review queued messages (interest due and short-term maturity). Approve, then send via Zixflow."
        actions={
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => load()} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" color="secondary" startIcon={<PlayArrow />} onClick={handleRunBatch}>
              Run batch (today)
            </Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Configure <code>WHATSAPP_API_KEY</code>, <code>WHATSAPP_PHONE_ID</code>, and template <code>payment_reminder</code> on the server.
        Daily cron creates <strong>PENDING_APPROVAL</strong> rows; sending happens only after you approve.
      </Alert>

      <DataToolbar>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DataToolbar>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Batch date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Borrower</TableCell>
              <TableCell>WhatsApp</TableCell>
              <TableCell>Debit</TableCell>
              <TableCell align="right">Rate / Interest</TableCell>
              <TableCell align="right">Due / Amount</TableCell>
              <TableCell>Preview</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <EmptyState title="No reminders" description="No rows match the selected status filter." />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.batchRunDate || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.reminderType} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.borrowerName || row.borrowerId}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.whatsappNumber}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {(row.debitId || '').slice(0, 8)}…
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.reminderType === 'INTEREST_DUE' ? (
                      <>
                        <Typography variant="body2">{row.interestRate ?? '—'}%</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(row.monthlyInterestAmount ?? 0)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        maturity
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.dueDate || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(row.amountDue ?? row.outstanding ?? 0)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Tooltip title={row.templateBody1 || ''}>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setDetailRow(row);
                          setDetailOpen(true);
                        }}
                      >
                        {row.templatePaymentDetails || row.templateBody1 || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={statusColor(row.status)} />
                    {row.providerError && (
                      <Typography variant="caption" color="error" display="block" noWrap>
                        {row.providerError}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                      <Tooltip title="View full message">
                        <IconButton size="small" onClick={() => { setDetailRow(row); setDetailOpen(true); }}>
                          <Typography variant="caption" sx={{ px: 0.5 }}>i</Typography>
                        </IconButton>
                      </Tooltip>
                      {row.status === 'PENDING_APPROVAL' && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={actionId === row.id}
                            onClick={() =>
                              wrapAction(row.id, () => approveWhatsappReminder(row.id), 'Approved')
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionId === row.id}
                            onClick={() => openReject(row)}
                          >
                            Reject
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<Send />}
                            disabled={actionId === row.id}
                            onClick={() =>
                              wrapAction(
                                row.id,
                                () => approveAndSendWhatsappReminder(row.id),
                                'Approved and sent'
                              )
                            }
                          >
                            Approve &amp; send
                          </Button>
                        </>
                      )}
                      {row.status === 'APPROVED' && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Send />}
                          disabled={actionId === row.id}
                          onClick={() =>
                            wrapAction(row.id, () => sendWhatsappReminder(row.id), 'Sent via WhatsApp')
                          }
                        >
                          Send
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject reminder</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Reason (optional)"
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmReject} disabled={!!actionId}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Message preview</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">payment_details (template variable)</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">{detailRow.templatePaymentDetails}</Typography>
              </Paper>
              <Typography variant="subtitle2" color="text.secondary">body_1</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" whiteSpace="pre-wrap">{detailRow.templateBody1}</Typography>
              </Paper>
              {(detailRow.providerRequestAt || detailRow.providerRequestPayload) && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Provider request (audit)</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Request at: {detailRow.providerRequestAt ? new Date(detailRow.providerRequestAt).toISOString() : '—'}
                    {detailRow.providerResponseAt && (
                      <> · Response at: {new Date(detailRow.providerResponseAt).toISOString()}</>
                    )}
                  </Typography>
                  {detailRow.providerRequestPayload && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {detailRow.providerRequestPayload}
                      </Typography>
                    </Paper>
                  )}
                </>
              )}
              {(detailRow.providerResponse || detailRow.providerError) && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Provider response (audit)</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: detailRow.providerError ? '#ffebee' : 'grey.50' }}>
                    {detailRow.providerError && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {detailRow.providerError}
                      </Typography>
                    )}
                    {detailRow.providerResponse && (
                      <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {detailRow.providerResponse}
                      </Typography>
                    )}
                  </Paper>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsappPaymentRemindersAdmin;
